import { useState, useRef, useEffect } from 'react';
import {
  IconPlus, IconEdit, IconArrowLeft, IconWorldUpload,
  IconTrash, IconDeviceFloppy, IconCloudUpload, IconFileImport,
} from '@tabler/icons-react';
import EtsyImport from './EtsyImport';
import { CATEGORIES } from '../lib/etsyImport';
import { useProducts } from '../context/ProductsContext';
import { useSiteContent, useCollectionPages } from '../context/SiteContentContext';
import { withIds } from './AdminPages';
import { ImageGrid } from '../components/ui/ImageUploader';
import {
  uploadProductImage, deleteProductImage, MAX_PRODUCT_IMAGES,
  listLocalProductIds, getLocalProductImages, moveLocalProductPhotosOnline,
} from '../lib/imageStore';
import { showToast } from '../components/ui/Toast';

function countLocalPhotos(products) {
  var ids = listLocalProductIds();
  var count = 0;
  ids.forEach(function(id) {
    if (products.some(function(p) { return String(p.id) === id; })) count += getLocalProductImages(id).length;
  });
  return count;
}

// "Wallet, gift for him , wallet" -> ['Wallet', 'gift for him'] (repeats dropped)
export function parseTags(text) {
  var seen = {};
  var tags = [];
  String(text || '').split(',').forEach(function(part) {
    var tag = part.trim().replace(/\s+/g, ' ');
    var key = tag.toLowerCase();
    if (tag && !seen[key]) { seen[key] = true; tags.push(tag); }
  });
  return tags;
}

function moveSummaryText(s) {
  var parts = [];
  if (s.moved) parts.push('Moved ' + s.moved + ' photo' + (s.moved !== 1 ? 's' : '') + ' online for ' + s.products + ' product' + (s.products !== 1 ? 's' : '') + '.');
  if (s.skipped) parts.push(s.skipped + ' photo' + (s.skipped !== 1 ? 's were' : ' was') + ' left out (no room for more than 6, or the product no longer exists).');
  if (s.errors.length) parts.push('Not moved: ' + s.errors.join(' '));
  if (!parts.length) parts.push('There were no old photos to move.');
  return parts.join(' ');
}

export default function AdminProducts() {
  var productsCtx = useProducts();
  var allProducts = productsCtx.allProducts;
  var saveProduct = productsCtx.saveProduct;
  var deleteProduct = productsCtx.deleteProduct;
  var _editing = useState(null);
  var editing = _editing[0]; var setEditing = _editing[1];
  var [moving, setMoving] = useState(false);
  var [importing, setImporting] = useState(false);
  var [, setRefresh] = useState(0);
  // Ticked product ids for "Make live / Make draft" on several at once
  var [ticked, setTicked] = useState([]);
  var [bulkSaving, setBulkSaving] = useState(false);
  var [categoryMenu, setCategoryMenu] = useState(false);

  var localPhotoCount = countLocalPhotos(allProducts);

  // Only ids still in the list count (a product may have been deleted)
  var tickedIds = ticked.filter(function(id) {
    return allProducts.some(function(p) { return p.id === id; });
  });
  var allTicked = allProducts.length > 0 && tickedIds.length === allProducts.length;

  function toggleTick(id) {
    setTicked(function(prev) {
      return prev.indexOf(id) >= 0 ? prev.filter(function(x) { return x !== id; }) : prev.concat([id]);
    });
  }

  function toggleAll() {
    setTicked(allTicked ? [] : allProducts.map(function(p) { return p.id; }));
  }

  // Saves one change (e.g. { status: 'live' }) on every ticked product.
  // successText(n) is the toast when all of them saved.
  async function bulkSave(changes, successText) {
    if (bulkSaving || !tickedIds.length) return;
    setBulkSaving(true);
    var ids = tickedIds.slice();
    var names = {};
    allProducts.forEach(function(p) { names[p.id] = p.name; });
    var failed = [];
    for (var i = 0; i < ids.length; i++) {
      // Send only the id and the changed field, so nothing else on the product is touched
      var res;
      try {
        res = await saveProduct(Object.assign({ id: ids[i] }, changes));
      } catch (err) {
        res = { error: 'Could not save. Please check your internet connection and try again.' };
      }
      if (!res || res.error) failed.push({ id: ids[i], name: names[ids[i]] || 'A product', error: res && res.error });
    }
    setBulkSaving(false);
    var done = ids.length - failed.length;
    if (!failed.length) {
      setTicked([]);
      showToast(successText(done));
      return;
    }
    // Failed ones stay ticked so Andre can try again
    setTicked(failed.map(function(f) { return f.id; }));
    showToast((done ? done + ' updated, but ' : '') + 'these could not be changed: '
      + failed.map(function(f) { return f.name; }).join(', ') + '. ' + (failed[0].error || 'Please try again.'), 'error');
  }

  function bulkSetStatus(status) {
    return bulkSave({ status: status }, function(n) {
      if (n === 1) return '1 product is now ' + (status === 'live' ? 'live' : 'a draft');
      return n + ' products are now ' + (status === 'live' ? 'live' : 'drafts');
    });
  }

  function bulkSetCategory(category) {
    setCategoryMenu(false);
    return bulkSave({ category: category }, function(n) {
      return n === 1 ? '1 product is now in ' + category : n + ' products are now in ' + category;
    });
  }

  async function moveOldPhotos() {
    if (moving) return;
    setMoving(true);
    var summary = await moveLocalProductPhotosOnline(allProducts, saveProduct);
    setMoving(false);
    setRefresh(function(n) { return n + 1; });
    showToast(moveSummaryText(summary), summary.errors.length ? 'error' : undefined);
  }

  if (importing) {
    return <EtsyImport onClose={function() { setImporting(false); }} />;
  }

  if (editing) {
    return <ProductForm
      product={editing}
      saveProduct={saveProduct}
      onBack={function() { setEditing(null); }}
      onDelete={function(id, images) {
        deleteProduct(id).then(async function(res) {
          if (res.error) { showToast(res.error, 'error'); return; }
          setEditing(null);
          var failed = 0;
          for (var i = 0; i < images.length; i++) {
            var del = await deleteProductImage(images[i]);
            if (del.error) failed++;
          }
          showToast(failed ? 'Product deleted, but ' + failed + ' photo file(s) could not be removed from storage.' : 'Product deleted', failed ? 'error' : undefined);
        });
      }}
      onDone={function() { setEditing(null); }}
    />;
  }

  function newProduct() {
    setEditing({
      id: null, name: '', slug: '', category: 'Briefcase', description: '',
      materials: '', dimensions: '', base_price: 0, compare_price: null,
      stock: 0, low_stock_threshold: 2, status: 'draft',
      sort_order: allProducts.length + 1,
      variants: [], colours: [], images: [],
      sku: '', tags: [], etsy_listing_id: null
    });
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div>
          <h1 className="adm-title">Products</h1>
          <p className="adm-sub" style={{ marginBottom: 0 }}>Click a product to edit it, or add a new one.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
          <button className="adm-btn adm-btn--outline" onClick={function() { setImporting(true); }}><IconFileImport size={14} /> Import from Etsy CSV</button>
          <button className="adm-btn" onClick={newProduct}><IconPlus size={14} /> Add product</button>
        </div>
      </div>

      {localPhotoCount > 0 && (
        <div className="adm-card" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
          <p style={{ fontSize: 12, color: '#555', lineHeight: 1.5, margin: 0, flex: '1 1 240px' }}>
            {localPhotoCount + ' product photo' + (localPhotoCount !== 1 ? 's are' : ' is') + ' still saved only in this browser, so customers can’t see '
              + (localPhotoCount !== 1 ? 'them' : 'it') + '. Move them online to show them on the shop.'}
          </p>
          <button className="adm-btn" disabled={moving} onClick={moveOldPhotos} style={{ opacity: moving ? 0.6 : 1 }}>
            <IconCloudUpload size={14} /> {moving ? 'Moving photos...' : 'Move old photos online'}
          </button>
        </div>
      )}

      {tickedIds.length > 0 && (
        <div className="adm-card" role="region" aria-label="Selected products"
          style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '10px 14px' }}>
          <span style={{ fontSize: 13, fontWeight: 500, marginRight: 'auto' }}>{tickedIds.length + ' selected'}</span>
          <button className="adm-btn" disabled={bulkSaving} onClick={function() { bulkSetStatus('live'); }} style={{ opacity: bulkSaving ? 0.6 : 1 }}>
            <IconWorldUpload size={14} /> {bulkSaving ? 'Saving...' : 'Make live'}
          </button>
          <button className="adm-btn adm-btn--outline" disabled={bulkSaving} onClick={function() { bulkSetStatus('draft'); }} style={{ opacity: bulkSaving ? 0.6 : 1 }}>
            <IconDeviceFloppy size={14} /> Make draft
          </button>
          <div style={{ position: 'relative' }}>
            <button className="adm-btn adm-btn--outline" disabled={bulkSaving} aria-haspopup="menu" aria-expanded={categoryMenu}
              onClick={function() { setCategoryMenu(!categoryMenu); }} style={{ opacity: bulkSaving ? 0.6 : 1 }}>
              {'Set category ▾'}
            </button>
            {categoryMenu && !bulkSaving && (
              <div role="menu" aria-label="Set category"
                style={{ position: 'absolute', top: '100%', right: 0, marginTop: 4, zIndex: 20, background: '#fff', border: '1px solid rgba(74,46,26,0.15)', borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.12)', minWidth: 150, padding: 4 }}>
                {CATEGORIES.map(function(c) {
                  return (
                    <button key={c} role="menuitem" onClick={function() { bulkSetCategory(c); }}
                      style={{ display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px', border: 0, background: 'transparent', fontSize: 13, cursor: 'pointer', borderRadius: 4 }}
                      onMouseEnter={function(e) { e.currentTarget.style.background = 'rgba(30,58,47,0.07)'; }}
                      onMouseLeave={function(e) { e.currentTarget.style.background = 'transparent'; }}>
                      {c}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <button className="adm-btn adm-btn--outline" disabled={bulkSaving} onClick={function() { setTicked([]); setCategoryMenu(false); }} style={{ opacity: bulkSaving ? 0.6 : 1 }}>
            Clear
          </button>
        </div>
      )}

      <div className="adm-card" style={{ padding: 0, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: 'rgba(74,46,26,0.04)', fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.1em', color: '#888' }}>
              <th style={{ padding: '10px 0 10px 14px', width: 18 }}>
                <input type="checkbox" aria-label="Tick all products" checked={allTicked} disabled={!allProducts.length || bulkSaving}
                  onChange={toggleAll} style={{ width: 15, height: 15, accentColor: '#1E3A2F', cursor: 'pointer' }} />
              </th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Product</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Price</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Status</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}>Stock</th>
              <th style={{ padding: '10px 14px', textAlign: 'left', fontWeight: 500 }}></th>
            </tr>
          </thead>
          <tbody>
            {allProducts.map(function(p) {
              var thumb = (p.images || [])[0];
              var isTicked = tickedIds.indexOf(p.id) >= 0;
              return (
                <tr key={p.id} onClick={function() { setEditing(Object.assign({}, p)); }} style={{ borderBottom: '1px solid rgba(74,46,26,0.08)', cursor: 'pointer', background: isTicked ? 'rgba(30,58,47,0.05)' : undefined }}>
                  <td onClick={function(e) { e.stopPropagation(); if (!bulkSaving) toggleTick(p.id); }} style={{ padding: '12px 0 12px 14px', width: 18, cursor: 'pointer' }}>
                    <input type="checkbox" aria-label={'Tick ' + p.name} checked={isTicked} disabled={bulkSaving}
                      onClick={function(e) { e.stopPropagation(); }}
                      onChange={function() { toggleTick(p.id); }}
                      style={{ width: 15, height: 15, accentColor: '#1E3A2F', cursor: 'pointer' }} />
                  </td>
                  <td style={{ padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 36, height: 36, borderRadius: 6, background: '#E0D9CC', overflow: 'hidden', flexShrink: 0 }}>
                      {thumb && <img src={thumb} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500 }}>{p.name}</div>
                      <div style={{ fontSize: 11, color: '#888' }}>{p.category + ' \u00b7 ' + p.colours.length + ' colour' + (p.colours.length !== 1 ? 's' : '')}</div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px', color: 'var(--color-secondary)', fontWeight: 500 }}>{'$' + p.base_price}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{
                      fontSize: 10, padding: '3px 9px', borderRadius: 20, letterSpacing: '0.07em',
                      background: p.status === 'live' ? '#E6F4EC' : '#f0f0f0',
                      color: p.status === 'live' ? '#1E6B3A' : '#888'
                    }}>{p.status === 'live' ? 'Live' : 'Draft'}</span>
                  </td>
                  <td style={{ padding: '12px 14px', fontSize: 12, color: '#888' }}>{p.stock + ' left'}</td>
                  <td style={{ padding: '12px 14px' }}><IconEdit size={15} color="#888" /></td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ProductForm(props) {
  var product = props.product;
  var saveProduct = props.saveProduct;
  var onBack = props.onBack;
  var onDelete = props.onDelete;
  var onDone = props.onDone;

  var _form = useState(Object.assign({}, product, { images: (product.images || []).filter(Boolean) }));
  var form = _form[0]; var setForm = _form[1];
  var images = form.images;
  var imagesRef = useRef(images);
  imagesRef.current = images;
  var [uploading, setUploading] = useState(0);
  // Photos uploaded in this visit (deleted again if Andre leaves without saving)
  var newUploads = useRef([]);
  // Saved photos Andre removed or replaced (deleted from storage after Save)
  var removedSaved = useRef([]);

  var allPages = withIds(useCollectionPages().pages);
  var saveContent = useSiteContent().save;
  var _selectedPageIds = useState(function() {
    if (!form.id) return [];
    return allPages.filter(function(page) {
      return (page.product_ids || []).indexOf(form.id) >= 0;
    }).map(function(page) { return page.id; });
  });
  var selectedPageIds = _selectedPageIds[0]; var setSelectedPageIds = _selectedPageIds[1];
  var [saving, setSaving] = useState(false);
  // Tags are typed as one comma list and turned into a list on save
  var [tagsText, setTagsText] = useState((product.tags || []).join(', '));

  function set(key, value) {
    setForm(function(prev) { return Object.assign({}, prev, { [key]: value }); });
  }

  function setImages(fn) {
    setForm(function(prev) { return Object.assign({}, prev, { images: fn(prev.images) }); });
  }

  // A photo leaves the product: new uploads are deleted now, saved ones after Save.
  function letGo(url) {
    if (!url) return;
    var at = newUploads.current.indexOf(url);
    if (at >= 0) {
      newUploads.current.splice(at, 1);
      deleteProductImage(url);
    } else {
      removedSaved.current.push(url);
    }
  }

  async function handleImageAdd(files, replaceIndex) {
    var list = files;
    var isReplace = replaceIndex !== null && replaceIndex !== undefined && replaceIndex < images.length;
    if (!isReplace) {
      var room = MAX_PRODUCT_IMAGES - images.length - uploading;
      if (room <= 0) {
        showToast('A product can have at most ' + MAX_PRODUCT_IMAGES + ' photos. Remove one first.', 'error');
        return;
      }
      if (files.length > room) {
        showToast('Only ' + room + ' more photo' + (room !== 1 ? 's' : '') + ' fit (6 at most). The rest were not added.', 'error');
        list = files.slice(0, room);
      }
    } else {
      list = files.slice(0, 1);
    }

    // Remember WHICH photo is being replaced, not its slot: Andre may
    // remove or reorder photos while the upload is running.
    var replacing = isReplace ? imagesRef.current[replaceIndex] : null;

    setUploading(function(n) { return n + list.length; });
    var results = await Promise.all(list.map(function(file) { return uploadProductImage(file, form.id); }));
    setUploading(function(n) { return n - list.length; });

    var urls = [];
    var errors = [];
    results.forEach(function(r) {
      if (r.error) errors.push(r.error); else urls.push(r.url);
    });
    if (errors.length) showToast(errors[0], 'error');
    if (!urls.length) return;

    var current = imagesRef.current;
    if (replacing && current.indexOf(replacing) >= 0) {
      letGo(replacing);
      newUploads.current = newUploads.current.concat(urls);
      setImages(function(prev) {
        var at = prev.indexOf(replacing);
        if (at < 0) return prev.concat(urls).slice(0, MAX_PRODUCT_IMAGES);
        var next = prev.slice();
        next[at] = urls[0];
        return next;
      });
      return;
    }

    // Adding (or the photo being replaced has gone): add at the end, 6 at most.
    var fit = urls.slice(0, Math.max(0, MAX_PRODUCT_IMAGES - current.length));
    var extra = urls.slice(fit.length);
    extra.forEach(function(url) { deleteProductImage(url); });
    if (extra.length) showToast('A product can have at most ' + MAX_PRODUCT_IMAGES + ' photos. ' + extra.length + ' photo(s) were not added.', 'error');
    if (!fit.length) return;
    newUploads.current = newUploads.current.concat(fit);
    setImages(function(prev) { return prev.concat(fit).slice(0, MAX_PRODUCT_IMAGES); });
  }

  function handleImageRemove(index) {
    letGo(images[index]);
    setImages(function(prev) { return prev.filter(function(_, i) { return i !== index; }); });
  }

  function handleImageReorder(from, to) {
    setImages(function(prev) {
      if (from < 0 || from >= prev.length || to < 0 || to >= prev.length || from === to) return prev;
      var next = prev.slice();
      var moved = next.splice(from, 1)[0];
      next.splice(to, 0, moved);
      return next;
    });
  }

  // Leaving the form any way (Back, the sidebar, after Delete) without saving:
  // tidy away photos uploaded during this visit. After a save the list is empty.
  useEffect(function() {
    var uploads = newUploads;
    return function() {
      uploads.current.forEach(function(url) { deleteProductImage(url); });
      uploads.current = [];
    };
  }, []);

  function handleBack() {
    onBack();
  }

  function togglePage(pageId) {
    var ids = selectedPageIds.slice();
    var index = ids.indexOf(pageId);
    if (index >= 0) { ids.splice(index, 1); } else { ids.push(pageId); }
    setSelectedPageIds(ids);
  }

  async function handleSave(status) {
    if (saving) return;
    if (uploading > 0) {
      showToast('Please wait until the photos have finished uploading.', 'error');
      return;
    }
    if (!form.name || !form.name.trim()) {
      showToast('Please give the product a name first.', 'error');
      return;
    }
    var slug = form.slug || form.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
    var sku = String(form.sku || '').trim();
    var productData = Object.assign({}, form, {
      slug: slug,
      status: status || form.status,
      sku: sku || null,
      tags: parseTags(tagsText),
    });

    // Save to the database and wait for the answer
    setSaving(true);
    var result = await saveProduct(productData);
    setSaving(false);

    if (result.error) {
      showToast(result.error, 'error');
      return;
    }

    // The photos are now part of the saved product; old ones can go.
    newUploads.current = [];
    var oldPhotos = removedSaved.current;
    removedSaved.current = [];
    var photoDeleteFailed = 0;
    for (var r = 0; r < oldPhotos.length; r++) {
      var del = await deleteProductImage(oldPhotos[r]);
      if (del.error) photoDeleteFailed++;
    }

    // The database gives new products their ID
    var actualId = result.data.id;

    // Update collection pages (site_content.collection_pages in Supabase)
    var pagesError = null;
    if (actualId) {
      var pagesChanged = false;
      var nextPages = allPages.map(function(page) {
        var ids = page.product_ids || [];
        var hasProduct = ids.indexOf(actualId) >= 0;
        var shouldHave = selectedPageIds.indexOf(page.id) >= 0;
        if (shouldHave && !hasProduct) {
          pagesChanged = true;
          return Object.assign({}, page, { product_ids: ids.concat([actualId]) });
        }
        if (!shouldHave && hasProduct) {
          pagesChanged = true;
          return Object.assign({}, page, { product_ids: ids.filter(function(pid) { return pid !== actualId; }) });
        }
        return page;
      });
      if (pagesChanged) {
        var pagesResult = await saveContent({ collection_pages: nextPages });
        if (pagesResult && pagesResult.error) pagesError = pagesResult.error;
      }
    }

    var problems = [];
    if (pagesError) problems.push('the page links could not be saved (' + pagesError + ')');
    if (photoDeleteFailed) problems.push(photoDeleteFailed + ' removed photo file(s) could not be deleted from storage');
    showToast(problems.length
      ? 'Product saved, but ' + problems.join(', and ') + '.'
      : 'Product saved', problems.length ? 'error' : undefined);
    onDone();
  }

  function addVariant() {
    var variants = form.variants.concat([{ id: Date.now(), size_name: '', price: 0, sort_order: form.variants.length + 1 }]);
    set('variants', variants);
  }

  function updateVariant(index, field, value) {
    var variants = form.variants.slice();
    variants[index] = Object.assign({}, variants[index], { [field]: field === 'price' ? Number(value) : value });
    set('variants', variants);
  }

  function removeVariant(index) {
    set('variants', form.variants.filter(function(_, i) { return i !== index; }));
  }

  function addColour() {
    var hex = document.getElementById('nc').value;
    var name = document.getElementById('ncn').value || 'Custom';
    var colours = form.colours.concat([{ id: Date.now(), name: name, hex_code: hex, stock: 0, sort_order: form.colours.length + 1 }]);
    set('colours', colours);
    document.getElementById('ncn').value = '';
  }

  function removeColour(index) {
    set('colours', form.colours.filter(function(_, i) { return i !== index; }));
  }

  return (
    <div>
      <button className="adm-btn adm-btn--outline" onClick={handleBack} style={{ marginBottom: 16 }}>
        <IconArrowLeft size={14} /> Back to products
      </button>
      <h1 className="adm-title">{form.id ? 'Edit: ' + form.name : 'New product'}</h1>
      <p className="adm-sub">Changes update the live product page when you publish.</p>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 16 }}>
        <div>
          <div className="adm-card">
            <div className="adm-card-title">Product details</div>
            <div className="adm-field"><label className="adm-label">Product name</label><input className="adm-input" value={form.name} onChange={function(e) { set('name', e.target.value); }} /></div>
            <div className="adm-field"><label className="adm-label">Category</label>
              <select className="adm-select" value={form.category} onChange={function(e) { set('category', e.target.value); }}>
                {(CATEGORIES.indexOf(form.category) < 0 && form.category ? [form.category] : []).concat(CATEGORIES).map(function(c) { return <option key={c}>{c}</option>; })}
              </select>
            </div>
            <div className="adm-field"><label className="adm-label">Description</label><textarea className="adm-textarea" value={form.description} onChange={function(e) { set('description', e.target.value); }} /></div>
            <div className="adm-field"><label className="adm-label">Materials</label><input className="adm-input" value={form.materials} onChange={function(e) { set('materials', e.target.value); }} /></div>
            <div className="adm-field"><label className="adm-label">Dimensions</label><input className="adm-input" value={form.dimensions} onChange={function(e) { set('dimensions', e.target.value); }} /></div>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">SKU, tags and Etsy</div>
            <div className="adm-field">
              <label className="adm-label" htmlFor="pf-sku">SKU (optional)</label>
              <input id="pf-sku" className="adm-input" value={form.sku || ''} placeholder="Your own stock code, e.g. BRF-001" onChange={function(e) { set('sku', e.target.value); }} />
            </div>
            <div className="adm-field">
              <label className="adm-label" htmlFor="pf-tags">Tags</label>
              <input id="pf-tags" className="adm-input" value={tagsText} placeholder="e.g. leather briefcase, gift for him" onChange={function(e) { setTagsText(e.target.value); }} />
              <p style={{ fontSize: 10, color: '#888', marginTop: 4, lineHeight: 1.5 }}>Separate tags with commas.</p>
            </div>
            <div className="adm-field" style={{ marginBottom: 0 }}>
              <label className="adm-label" htmlFor="pf-etsy">From Etsy listing #</label>
              <input id="pf-etsy" className="adm-input" readOnly value={form.etsy_listing_id || ''} placeholder="Not imported from Etsy"
                style={{ background: '#f5f5f0', color: '#555', cursor: 'default' }} />
              {form.etsy_listing_id && (
                <a href={'https://www.etsy.com/listing/' + encodeURIComponent(form.etsy_listing_id)} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 11, color: 'var(--color-secondary)', display: 'inline-block', marginTop: 4 }}>View on Etsy</a>
              )}
            </div>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">Product images</div>
            <ImageGrid images={images} onAdd={handleImageAdd} onRemove={handleImageRemove} onReorder={handleImageReorder} uploading={uploading} maxSlots={MAX_PRODUCT_IMAGES} />
            <p style={{ fontSize: 10, color: '#888', marginTop: 8, lineHeight: 1.5 }}>Up to 6 photos, under 5 MB each. The first (Main) is shown in the catalogue. Drag a photo to change the order. Photos go live when you save.</p>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">Size variations</div>
            {form.variants.map(function(v, i) {
              return (
                <div key={v.id} style={{ display: 'grid', gridTemplateColumns: '1fr 80px 28px', gap: 8, marginBottom: 8, alignItems: 'center' }}>
                  <input className="adm-input" value={v.size_name} onChange={function(e) { updateVariant(i, 'size_name', e.target.value); }} placeholder="Size name" />
                  <input className="adm-input" type="number" value={v.price} onChange={function(e) { updateVariant(i, 'price', e.target.value); }} />
                  <button onClick={function() { removeVariant(i); }} style={{ background: 'none', border: 'none', color: '#999', fontSize: 16, cursor: 'pointer' }}>{'\u00d7'}</button>
                </div>
              );
            })}
            <button className="adm-btn adm-btn--outline" onClick={addVariant} style={{ width: '100%', justifyContent: 'center' }}>
              <IconPlus size={12} /> Add size
            </button>
          </div>
        </div>

        <div>
          <div className="adm-card">
            <div className="adm-card-title">Pricing</div>
            <div className="adm-field"><label className="adm-label">Base price (USD)</label><input className="adm-input" type="number" value={form.base_price} onChange={function(e) { set('base_price', Number(e.target.value)); }} style={{ fontSize: 16, fontWeight: 500 }} /></div>
            <div className="adm-field"><label className="adm-label">Compare-at price (optional)</label><input className="adm-input" type="number" value={form.compare_price || ''} onChange={function(e) { set('compare_price', e.target.value ? Number(e.target.value) : null); }} /></div>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">Colour options</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
              {form.colours.map(function(c, i) {
                return (
                  <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 5, background: '#f5f5f0', border: '1px solid rgba(74,46,26,0.1)', borderRadius: 20, padding: '4px 10px 4px 6px', fontSize: 11 }}>
                    <div style={{ width: 12, height: 12, borderRadius: '50%', background: c.hex_code, border: '1px solid rgba(0,0,0,0.1)' }}></div>
                    {c.name}
                    <span onClick={function() { removeColour(i); }} style={{ marginLeft: 4, cursor: 'pointer', color: '#999' }}>{'\u00d7'}</span>
                  </div>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <input type="color" id="nc" defaultValue="#8B2020" style={{ width: 32, height: 28, padding: 2, borderRadius: 4, cursor: 'pointer' }} />
              <input id="ncn" className="adm-input" placeholder="Colour name" style={{ flex: 1, fontSize: 12 }} />
              <button className="adm-btn adm-btn--outline" style={{ fontSize: 11, padding: '6px 10px' }} onClick={addColour}>Add</button>
            </div>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">Stock</div>
            <div className="adm-field"><label className="adm-label">Units available</label><input className="adm-input" type="number" value={form.stock} onChange={function(e) { set('stock', Number(e.target.value)); }} /></div>
            <div className="adm-field"><label className="adm-label">Low stock alert threshold</label><input className="adm-input" type="number" value={form.low_stock_threshold} onChange={function(e) { set('low_stock_threshold', Number(e.target.value)); }} /></div>
          </div>

          <div className="adm-card">
            <div className="adm-card-title">Pages</div>
            {allPages.length === 0 ? (
              <p style={{ fontSize: 12, color: '#888', lineHeight: 1.6 }}>
                No pages created yet. Go to <strong>Pages</strong> in the sidebar to create your first collection page, then come back here to assign this product.
              </p>
            ) : (
              <div>
                <p style={{ fontSize: 11, color: '#888', marginBottom: 10, lineHeight: 1.5 }}>
                  Tick the pages where this product should appear.
                </p>
                {allPages.slice().sort(function(a, b) { return a.sort_order - b.sort_order; }).map(function(page) {
                  var isChecked = selectedPageIds.indexOf(page.id) >= 0;
                  var isDraft = page.status === 'draft';
                  return (
                    <label key={page.id} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '8px 0',
                      borderBottom: '1px solid rgba(74,46,26,0.06)', cursor: 'pointer',
                      opacity: isDraft ? 0.5 : 1,
                    }}>
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={function() { togglePage(page.id); }}
                        style={{ width: 14, height: 14, accentColor: '#1E3A2F' }}
                      />
                      <div>
                        <div style={{ fontSize: 12, fontWeight: 500, color: '#1A1A1A' }}>{page.name}</div>
                        <div style={{ fontSize: 10, color: '#888' }}>
                          {'Under ' + page.parent_nav + (isDraft ? ' \u00b7 Draft' : '')}
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>

          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            <button className="adm-btn adm-btn--outline" disabled={saving} onClick={function() { handleSave('draft'); }} style={{ flex: 1, justifyContent: 'center', opacity: saving ? 0.6 : 1 }}>
              <IconDeviceFloppy size={14} /> {saving ? 'Saving...' : 'Save as draft'}
            </button>
            <button className="adm-btn" disabled={saving} onClick={function() { handleSave('live'); }} style={{ flex: 1, justifyContent: 'center', opacity: saving ? 0.6 : 1 }}>
              <IconWorldUpload size={14} /> {saving ? 'Saving...' : 'Publish'}
            </button>
          </div>
          {form.id && (
            <button className="adm-btn adm-btn--danger" onClick={function() { if (window.confirm('Delete this product?')) onDelete(form.id, images.concat(removedSaved.current)); }} style={{ width: '100%', justifyContent: 'center', marginTop: 8 }}>
              <IconTrash size={14} /> Delete product
            </button>
          )}
        </div>
      </div>
    </div>
  );
}